import Utils from '.';
import { httpConstants } from '../constants';

/**
 * Defines the structure of HTTP request options.
 */

/**
 * Creates request options for fetch API calls.
 *
 * @param method - The HTTP method (e.g., 'GET', 'POST', etc.).
 * @param data - The data to be sent in the request body.
 * @param headers - Optional headers for the request.
 * @returns The formatted request options.
 */
const createRequestOptions = (
  method: string,
  data?: unknown,
  headers?: HeadersInit
): RequestInit => {
  const { GET } = httpConstants.METHOD_TYPE;

  // Remove Content-Type for FormData (browser sets it automatically)
  const isFormData = data instanceof FormData;
  const requestOptions: RequestInit = {
    method,
    headers: isFormData ? headers : { 'Content-Type': 'application/json', ...headers },
    body: method !== GET ? (isFormData ? data : JSON.stringify(data)) : undefined,
  };

  return requestOptions;
};

export const httpService = async <T>(
  method: string,
  url: string,
  data?: unknown,
  headers?: HeadersInit,
  is_auth: boolean = true
): Promise<T> => {
  const token = Utils.getCookie('auth-token');
  if (is_auth) {
    headers = {
      ...headers,
      Authorization: `Bearer ${token}`,
    };
  }
  const requestOptions = createRequestOptions(method, data, headers);

  try {
    const response = await fetch(url, requestOptions);
    if (!response.ok) {
      let errorBody: any = {};
      try {
        const contentType = response.headers.get('Content-Type');
        if (contentType?.includes('application/json')) {
          errorBody = await response.json();
        } else {
          errorBody = { detail: await response.text() };
        }
      } catch {
        errorBody = {};
      }
      // ─── global session-expiry fallback ───────────────────────────────
      if (response.status === 401 || response.status === 403) {
        // 1) clear tokens/cookies
        Utils.clearTokenCookies();

        // queue the toast for the login page

        // 2) Check if the current location is already a login page
        const currentUrl = window.location.pathname;
        const fullUrl = window.location.href;
        const isLoginPage = currentUrl.endsWith('/login') || currentUrl.includes('/login');

        let callbackSlug = null;

        if (currentUrl.endsWith('/callback') && fullUrl.includes('state=')) {
          const stateParam = fullUrl.split('state=')[1]?.split('&')[0];
          const parts = stateParam?.split('%26');
          if (parts?.length > 2) {
            callbackSlug = parts[2];
          }
        }

        localStorage.setItem(
          'LogoutToast',
          callbackSlug
            ? 'Email domain not authorized for this organization'
            : 'Your session has expired. Please sign in again.'
        );

        if (!isLoginPage) {
          // 3) redirect to tenant-aware login
          const slug = localStorage.getItem('tenant-slug') || 'thehackettgroup';
          window.location.href = `/${callbackSlug || slug}/login`;
        }
      }

      // still reject so callers can handle (page will reload anyway)
      return Promise.reject({
        status: response.status,
        message: errorBody?.detail || errorBody?.message || response.statusText,
        details: errorBody?.detail,
      });
    }

    const contentType = response.headers.get('Content-Type');

    if (contentType?.includes('application/json')) {
      return (await response.json()) as T;
    } else {
      return (await response.text()) as unknown as T;
    }
  } catch (err) {
    return Promise.reject(err);
  }
};
